import { createStyles, makeStyles } from "@material-ui/core";
import classnames from "classnames";
import * as React from "react";

import {
    camelize,
    DisplayField,
    Link,
    MessageComposerIconLink,
    PhoneLink,
    RelodeTheme,
    Text,
} from "../";
import { Stakeholder } from "../../domain";

const useDisplayStakeholderStyles = makeStyles((theme: RelodeTheme) =>
    createStyles({
        inline: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            position: "relative",
        },
        contactButton: {
            marginTop: 10,
            position: "absolute",
            right: 0,
        },
        messageIconWithPhone: {
            right: 32,
        },
        nameWithOneIcon: {
            marginRight: 32,
        },
        nameWithTwoIcons: {
            marginRight: 64,
        },
    })
);

type DisplayStakeholderProps = {
    label: string;
    stakeholder?: Stakeholder;
    hideWhenEmpty?: boolean;
    hideMessageLink?: boolean;
    hidePhoneLink?: boolean;
    showUserLink?: boolean;
    fullWidth?: boolean;
    showHeader?: boolean;
    headerText?: string;
    messageProps?: {
        jobId?: number;
        jobName?: string;
        leadId?: number;
        leadName?: string;
    };
};

export const DisplayStakeholder = (props: DisplayStakeholderProps) => {
    const {
        label,
        stakeholder,
        hideWhenEmpty,
        hideMessageLink,
        hidePhoneLink,
        showUserLink = false,
        messageProps,
        fullWidth,
        showHeader,
        headerText,
        ...passthrough
    } = props;

    const classes = useDisplayStakeholderStyles({});
    const dataCy = passthrough["data-cy"] ?? camelize(label);
    const messageIconCy = !!dataCy ? `${dataCy}-message` : undefined;
    const phoneIconCy = !!dataCy ? `${dataCy}-phone` : undefined;
    const textCy = !!dataCy ? `${dataCy}-text` : undefined;

    const nameClasses = classnames({
        [classes.inline]: true,
        [classes.nameWithOneIcon]:
            (hideMessageLink && !hidePhoneLink) ||
            (!hideMessageLink && hidePhoneLink),
        [classes.nameWithTwoIcons]: !hideMessageLink && !hidePhoneLink,
    });

    const msgClasses = classnames({
        [classes.contactButton]: true,
        [classes.messageIconWithPhone]: !hidePhoneLink,
    });

    if (!stakeholder && hideWhenEmpty) return null;

    return (
        <DisplayField
            label={label}
            showHeader={showHeader}
            headerText={headerText}
            fullWidth={fullWidth}
            data-cy={dataCy}
            component={
                !!stakeholder ? (
                    <div className={classes.inline}>
                        {showUserLink ? (
                            <Text variant="body1" className={nameClasses}>
                                <Link
                                    target="_blank"
                                    href={`${appConfig.goUrl}/user/details?userid=${stakeholder.userId}`}
                                    data-cy={textCy}
                                >
                                    {stakeholder.name}
                                </Link>
                            </Text>
                        ) : (
                            <Text
                                variant="body1"
                                className={nameClasses}
                                dataCy={textCy}
                            >
                                {stakeholder.name}
                            </Text>
                        )}
                        {!hideMessageLink ? (
                            <MessageComposerIconLink
                                className={msgClasses}
                                initialRecipients={[
                                    {
                                        userId: stakeholder.userId,
                                        displayName: stakeholder.name,
                                        roleName: stakeholder.role.roleName,
                                    },
                                ]}
                                jobId={messageProps?.jobId}
                                jobName={messageProps?.jobName}
                                leadId={messageProps?.leadId}
                                leadName={messageProps?.leadName}
                                title={`message this ${stakeholder.role.roleName}`}
                                data-cy={messageIconCy}
                            />
                        ) : null}
                        {stakeholder.phoneNumber && !hidePhoneLink ? (
                            <PhoneLink
                                phone={stakeholder.phoneNumber}
                                variant="iconOnly"
                                className={classes.contactButton}
                                data-cy={phoneIconCy}
                            />
                        ) : null}
                    </div>
                ) : (
                    <div className={classes.inline}>
                        <Text
                            variant="body1"
                            id="jobJobManager"
                            className={nameClasses}
                        >
                            n/a
                        </Text>
                    </div>
                )
            }
        />
    );
};
